import { Database, PanelsTopLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type {
  TemplateDashboardSummary,
  TemplateDatabaseSummary,
  TemplateSummary,
  TemplateTableSummary,
} from '@/api/types'
import { Badge } from '@/shared/ui/badge'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table'

/** The databases, tables, fields, views, sample rows and dashboards a template creates. */
export function TemplateContents({ summary }: { summary: TemplateSummary }) {
  const databaseNames = new Map(summary.databases.map((d) => [d.id, d.name]))

  return (
    <div className="divide-y">
      {summary.databases.map((database) => (
        <DatabaseContents key={database.id} database={database} />
      ))}
      {summary.dashboards.map((dashboard) => (
        <DashboardContents
          key={dashboard.id}
          dashboard={dashboard}
          databaseNames={databaseNames}
        />
      ))}
    </div>
  )
}

function DatabaseContents({ database }: { database: TemplateDatabaseSummary }) {
  return (
    <section className="space-y-4 py-6 first:pt-0 last:pb-0">
      <Heading icon={<Database className="size-4 shrink-0" />} name={database.name}>
        {database.description}
      </Heading>
      {database.tables.map((table) => (
        <TableContents key={table.id} table={table} />
      ))}
    </section>
  )
}

function TableContents({ table }: { table: TemplateTableSummary }) {
  const { t } = useTranslation()
  const fieldNames = new Map(table.fields.map((field) => [field.id, field.name]))
  const { field_ids: columns, rows } = table.sample_rows

  return (
    <div className="space-y-3 border-t pt-4">
      <div className="flex items-baseline gap-2">
        <h4 className="font-medium">{table.name}</h4>
        <span className="text-xs text-muted-foreground">
          {table.row_count > 0
            ? t('templates.count.rows', { count: table.row_count })
            : t('templates.structureOnly')}
        </span>
      </div>

      <Chips
        label={t('templates.fields')}
        items={table.fields.map((field) => ({
          id: field.id,
          name: field.name,
          kind: t(`fieldType.${field.type}` as 'fieldType.text', {
            defaultValue: field.type,
          }),
        }))}
      />
      {table.views.length > 0 && (
        <Chips
          label={t('templates.views')}
          items={table.views.map((view) => ({
            id: view.id,
            name: view.name,
            kind: t(`templates.viewType.${view.type}` as 'templates.viewType.grid', {
              defaultValue: view.type,
            }),
          }))}
        />
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableCaption className="mt-0 py-2 text-xs">
              {t('templates.sampleRowsOf', {
                shown: rows.length,
                total: table.row_count,
              })}
            </TableCaption>
            <TableHeader>
              <TableRow>
                {columns.map((id) => (
                  <TableHead key={id}>{fieldNames.get(id)}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={index}>
                  {row.map((value, column) => (
                    <TableCell key={columns[column]} className="max-w-48 truncate">
                      {value}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

function DashboardContents({
  dashboard,
  databaseNames,
}: {
  dashboard: TemplateDashboardSummary
  databaseNames: Map<number, string>
}) {
  const { t } = useTranslation()
  const reads = dashboard.database_ids
    .map((id) => databaseNames.get(id))
    .filter((name) => name !== undefined)

  return (
    <section className="space-y-3 py-6 first:pt-0 last:pb-0">
      <Heading icon={<PanelsTopLeft className="size-4 shrink-0" />} name={dashboard.name}>
        {dashboard.description}
      </Heading>
      {reads.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {t('templates.readsFrom', { names: reads.join(', ') })}
        </p>
      )}
      <Chips
        label={t('templates.widgets')}
        items={dashboard.widgets.map((widget) => ({
          id: widget.id,
          name: widget.title,
          kind: t(
            `templates.widgetType.${widget.type}` as 'templates.widgetType.summary',
            {
              defaultValue: widget.type,
            },
          ),
        }))}
      />
    </section>
  )
}

function Heading({
  icon,
  name,
  children,
}: {
  icon: React.ReactNode
  name: string
  children: string
}) {
  return (
    <div className="space-y-1">
      <h3 className="flex items-center gap-2 font-medium">
        {icon}
        {name}
      </h3>
      {children !== '' && <p className="text-sm text-muted-foreground">{children}</p>}
    </div>
  )
}

function Chips({
  label,
  items,
}: {
  label: string
  items: { id: number; name: string; kind: string }[]
}) {
  return (
    <div className="space-y-1.5">
      <h5 className="text-xs font-medium text-muted-foreground">{label}</h5>
      <ul aria-label={label} className="flex flex-wrap gap-1.5">
        {items.map((item) => (
          <li key={item.id}>
            <Badge variant="outline" className="font-normal">
              {item.name}
              <span className="text-muted-foreground">{item.kind}</span>
            </Badge>
          </li>
        ))}
      </ul>
    </div>
  )
}
